import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { AdminPageHeader } from '../Common/AdminPagesHeader';
import {
    QUERY_KEYS,
    ROLES,
    SAFETY_MEETING,
    SAFETY_MEETING_ASSOCIATION_COLUMN,
} from '../../../constants';
import { AddSafetyMeetingModal } from '../../shared/Modal/AddSafetyMeetingModal';
import { AssignToUserIcon, GreenPlus } from '../../../assets/svg';
import AssignSafetyMeetingModal from '../../shared/Modal/AssignSafetyMeetingModal';
import { usePagination } from '../../../hooks';
import { popup } from '../../../utils';
import { getAllSafetyMeetingAssociationService } from '../../../utils/apiServices/safetyServiceAPI';
import { Table } from '../../shared';
import useWindowSize from '../../../hooks/useWindowSize';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

const SafetyMeetings = () => {
    const [search, setSearch] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [safetyMeetingAssociation, setSafetyMeetingAssociation] = useState({
        data: [],
        count: 0,
    });
    const pagination = usePagination(
        safetyMeetingAssociation?.data.length,
        safetyMeetingAssociation?.count,
        50,
    );
    const { width } = useWindowSize();
    const role = useSelector((s) => s?.user?.role);
    const isAllowed = role !== ROLES.SALES_AGENT;

    const [debouncedSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error, refetch } = useQuery({
        queryKey: [
            QUERY_KEYS.SAFETY_MEETING_ASSOCIATION,
            debouncedSearch,
            pagination.limit,
            pagination.offset,
        ],
        queryFn: async () =>
            await getAllSafetyMeetingAssociationService({
                limit: pagination.limit,
                offset: pagination.offset,
                search: debouncedSearch,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
    });

    if (isError) popup('error', error.message);

    useEffect(() => {
        if (data && isFetched) {
            setSafetyMeetingAssociation(data);
        }
    }, [data, isFetched]);

    return (
        <>
            <AdminPageHeader
                showAddButton={isAllowed}
                heading={SAFETY_MEETING.PAGE_HEADING}
                buttonText={`Add ${width > 720 ? 'Safety' : ''} Meeting`}
                handleAddClick={() => setShowAddModal(true)}
                AddModal={
                    <AddSafetyMeetingModal
                        showAddModal={showAddModal}
                        setShowAddModal={setShowAddModal}
                    />
                }
                backbutton
                showSearchBar
                searchBarPlaceholder={SAFETY_MEETING.FILE_NAME_SEARCH}
                count={pagination.dataCount}
                setSearch={setSearch}
                headerButtonChildren={
                    isAllowed && (
                        <>
                            {width > 590 ? (
                                <button
                                    className='ssc-secondary-white-btn'
                                    type='button'
                                    data-bs-toggle='modal'
                                    data-bs-target='#exampleModal-add'
                                    onClick={() => setShowAssignModal(true)}
                                    title='Assign Safety Meeting'
                                >
                                    <GreenPlus />
                                    <p>{`Assign ${width > 720 ? 'Safety' : ''} Meeting`}</p>
                                </button>
                            ) : (
                                <span
                                    id='action-btn-green'
                                    title='Assign Safety Meeting'
                                    onClick={() => setShowAssignModal(true)}
                                >
                                    <AssignToUserIcon
                                        color='var(--col-btn-primary)'
                                        fill={'#fff'}
                                        size={20}
                                    />
                                </span>
                            )}
                        </>
                    )
                }
            />
            <Table
                columns={SAFETY_MEETING_ASSOCIATION_COLUMN}
                data={safetyMeetingAssociation?.data}
                pagination={pagination}
                pageName='Safety Meeting Associations'
                debouncedApiCall={refetch}
                stickyColumns={['Client']}
                isLoading={isFetching}
            />
            {showAssignModal && (
                <AssignSafetyMeetingModal
                    showModal={showAssignModal}
                    setShowModal={setShowAssignModal}
                />
            )}
        </>
    );
};

export default SafetyMeetings;
